import { AppAction, AppMiddleware } from '@typings/redux';
import { isRSAA, RSAA } from 'redux-api-middleware';
import { getAppConfig } from 'utils/config/appConfig';
import { ROUTE_AUTH_LOGIN } from 'config/routes';

// Refresh session function - same as working apps
const refreshSession = (): Promise<boolean> => {
  return fetch(`${getAppConfig('OSIN20_API_URL')}/token/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((response) => {
      if (response.status === 204) {
        return true;
      }
      return false;
    })
    .catch(() => {
      return false;
    });
};

// Check if this is an authentication-related endpoint that shouldn't trigger refresh
const isAuthEndpoint = (url: string): boolean => {
  const authEndpoints = ['/me', '/token/refresh', '/auth', '/logout'];
  return authEndpoints.some((endpoint) => url.includes(endpoint));
};

// Check if this action should preserve its custom fetch logic (like file downloads)
const shouldPreserveCustomFetch = (action: any): boolean => {
  // If the action already has a custom fetch function, preserve it
  // This prevents interfering with file downloads, custom error handling, etc.
  return !!action[RSAA].fetch;
};

// Check if user is currently on an auth page
const isOnAuthPage = (): boolean => {
  return window.location.pathname.startsWith('/auth');
};

// Custom fetch wrapper with retry logic for 401 responses - same as working apps
const fetchWithRetry = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  let retryCount = 0;
  const maxRetries = 3;
  let hasRefreshed = false; // Track if we've already called refresh

  while (retryCount < maxRetries) {
    const response = await fetch(input, init);

    // If we get a 401, handle it
    if (response.status === 401) {
      // Don't trigger refresh for auth endpoints to prevent infinite loops
      if (isAuthEndpoint(input.toString())) {
        return response; // Return 401 immediately for auth endpoints
      }

      // Only call refresh token once on the first 401 for non-auth endpoints
      if (!hasRefreshed) {
        refreshSession(); // Call refresh in background
        hasRefreshed = true;
      }

      // Retry the original request with EXACTLY the same parameters
      // This preserves all headers, method, body, credentials, etc.
      const retryResponse = await fetch(input, {
        ...init, // Preserve all original init options
        credentials: 'include', // Ensure credentials are always included
      });

      // If the retry succeeds (any status other than 401), return the response
      if (retryResponse.status !== 401) {
        return retryResponse;
      }

      retryCount++;
    } else {
      // If it's not a 401, return the response immediately
      return response;
    }
  }

  // If we've exhausted all retries, redirect to login ONLY if not already on auth page
  if (!isOnAuthPage()) {
    window.location.href = ROUTE_AUTH_LOGIN;
  }
  throw new Error('Authentication failed after retries');
};

export const customFetchMiddleware: AppMiddleware = () => (next) => (action: AppAction) => {
  if (!isRSAA(action)) {
    return next(action);
  }

  // Skip refresh logic on any auth-related page to prevent infinite loops
  if (isOnAuthPage()) {
    return next(action);
  }

  // If the action already has a custom fetch function, preserve it completely
  // This prevents interfering with file downloads, custom error handling, etc.
  if (shouldPreserveCustomFetch(action)) {
    return next(action);
  }

  // Only apply refresh logic to actions without custom fetch functions
  // This ensures we don't interfere with specialized fetch logic
  const wrappedFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    let retryCount = 0;
    const maxRetries = 3;
    let hasRefreshed = false;

    while (retryCount < maxRetries) {
      const response = await fetch(input, init);

      // If we get a 401, handle it
      if (response.status === 401) {
        // Don't trigger refresh for auth endpoints to prevent infinite loops
        if (isAuthEndpoint(input.toString())) {
          return response; // Return 401 immediately for auth endpoints
        }

        // Only call refresh token once on the first 401 for non-auth endpoints
        if (!hasRefreshed) {
          refreshSession(); // Call refresh in background
          hasRefreshed = true;
        }

        // Retry the original request with EXACTLY the same parameters
        // This preserves all headers, method, body, credentials, etc.
        const retryResponse = await fetch(input, {
          ...init, // Preserve all original init options
          credentials: 'include', // Ensure credentials are always included
        });

        // If the retry succeeds (any status other than 401), return the response
        if (retryResponse.status !== 401) {
          return retryResponse;
        }

        retryCount++;
      } else {
        // If it's not a 401, return the response immediately
        return response;
      }
    }

    // If we've exhausted all retries, redirect to login ONLY if not already on auth page
    if (!isOnAuthPage()) {
      window.location.href = ROUTE_AUTH_LOGIN;
    }
    throw new Error('Authentication failed after retries');
  };

  const wrappedAction = {
    ...action,
    [RSAA]: {
      ...action[RSAA],
      fetch: wrappedFetch,
    },
  };

  return next(wrappedAction);
};
